import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useWiserTheme } from "../../../hooks/useWiserTheme";

export type CriteriaKey =
  | "criticalCriteria"
  | "mustCriteria"
  | "optionalCriteria";

export interface CriteriaValues {
  criticalCriteria: string[];
  mustCriteria: string[];
  optionalCriteria: string[];
}

interface PositionCriteriaProps {
  value?: CriteriaValues;
  onChange?: (value: CriteriaValues) => void;
  errors?: Partial<Record<CriteriaKey, string>>;
  readOnly?: boolean;
}

const SECTIONS: { key: CriteriaKey; label: string; max: number }[] = [
  { key: "criticalCriteria", label: "Kritik Kriterler:", max: 5 },
  { key: "mustCriteria", label: "Aranan Kriterler:", max: 20 },
  { key: "optionalCriteria", label: "Destekleyici Kriterler:", max: 20 },
];

const EMPTY: CriteriaValues = {
  criticalCriteria: [],
  mustCriteria: [],
  optionalCriteria: [],
};

const getMaxCriteria = (key: CriteriaKey) =>
  SECTIONS.find((s) => s.key === key)?.max ?? 20;

const PositionCriteria: React.FC<PositionCriteriaProps> = ({
  value,
  onChange,
  errors,
  readOnly = false,
}) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const dividerColor = darkModeActive ? tokens.border : "#E2E8F0";
  const [internal, setInternal] = useState<CriteriaValues>(EMPTY);
  const [inputs, setInputs] = useState<Record<CriteriaKey, string>>({
    criticalCriteria: "",
    mustCriteria: "",
    optionalCriteria: "",
  });

  const criteria = value ?? internal;

  const updateCriteria = (next: CriteriaValues) => {
    if (onChange) onChange(next);
    else setInternal(next);
  };

  const addTag = (key: CriteriaKey) => {
    const val = inputs[key].replace(/,/g, "").trim();
    if (!val) return;
    if (criteria[key].length >= getMaxCriteria(key)) return;
    if (!criteria[key].includes(val)) {
      updateCriteria({ ...criteria, [key]: [...criteria[key], val] });
    }
    setInputs((prev) => ({ ...prev, [key]: "" }));
  };

  const removeTag = (key: CriteriaKey, index: number) => {
    updateCriteria({
      ...criteria,
      [key]: criteria[key].filter((_, i) => i !== index),
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: { xs: "wrap", md: "nowrap" },
        backgroundColor: darkModeActive ? "transparent" : "#F8FAFC",
        border: darkModeActive ? `1px solid ${tokens.border}` : "none",
        borderRadius: "8px",
        overflow: "hidden",
        mb: 3,
      }}
    >
      {SECTIONS.map((section, idx) => (
        <Box
          key={section.key}
          sx={{
            position: "relative",
            flex: 1,
            minWidth: { xs: "100%", md: 0 },
            p: "12px 22px 14px 20px",
            borderTop: {
              xs: idx > 0 ? `1px solid ${dividerColor}` : "none",
              md: "none",
            },
            ...(idx > 0 && {
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "12px",
                bottom: "12px",
                width: "1px",
                backgroundColor: dividerColor,
                display: { xs: "none", md: "block" },
              },
            }),
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              fontSize: 13,
              fontWeight: 500,
              color: darkModeActive ? tokens.textPrimary : "#64748B",
              mb: 1,
            }}
          >
            {section.label}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {criteria[section.key].map((tag, index) => (
              <Box
                key={`${tag}-${index}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  minHeight: "32px",
                  maxWidth: "100%",
                  px: "10px",
                  py: "6px",
                  borderRadius: "6px",
                  backgroundColor: "#2684FF",
                  color: "#FFFFFF",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "20px",
                  overflowWrap: "anywhere",
                  "& .MuiSvgIcon-root": { fontSize: "14px" },
                }}
              >
                {!readOnly && (
                  <Close
                    onClick={() => removeTag(section.key, index)}
                    sx={{
                      cursor: "pointer",
                      flexShrink: 0,
                      mt: "3px",
                      "&:hover": { opacity: 0.8 },
                    }}
                  />
                )}
                <Box component="span" sx={{ minWidth: 0 }}>
                  {tag}
                </Box>
              </Box>
            ))}

            {!readOnly && criteria[section.key].length < section.max && (
              <Box
                onClick={() => {
                  const input = document.getElementById(
                    `criteria-input-${section.key}`,
                  ) as HTMLInputElement | null;
                  input?.focus();
                }}
                sx={{
                  flex: 1,
                  minWidth: "90px",
                  display: "flex",
                  alignItems: "center",
                  height: "32px",
                  px: "10px",
                  border: `1px solid ${darkModeActive ? tokens.borderStrong : "#D1D5DB"}`,
                  borderRadius: "6px",
                  backgroundColor: darkModeActive ? tokens.inputBg : "#FFFFFF",
                  cursor: "text",
                  "&:focus-within": {
                    borderColor: darkModeActive ? tokens.accent : "#3B82F6",
                    boxShadow: darkModeActive
                      ? "none"
                      : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                  },
                }}
              >
                <input
                  id={`criteria-input-${section.key}`}
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={inputs[section.key]}
                  onChange={(e) =>
                    setInputs((prev) => ({
                      ...prev,
                      [section.key]: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag(section.key);
                    }
                  }}
                  onBlur={() => addTag(section.key)}
                  placeholder="Ekle..."
                  style={{
                    border: "none",
                    outline: "none",
                    backgroundColor: "transparent",
                    fontSize: "12px",
                    fontFamily: "Inter",
                    color: darkModeActive ? tokens.textPrimary : "#111827",
                    width: "100%",
                    padding: "0",
                  }}
                />
              </Box>
            )}
          </Box>

          {errors?.[section.key] && (
            <Typography color="error" fontSize={12} sx={{ mt: 0.5 }}>
              {errors[section.key]}
            </Typography>
          )}

          {!readOnly &&
            !errors?.[section.key] &&
            criteria[section.key].length >= section.max && (
              <Typography
                fontSize={12}
                sx={{
                  mt: 0.5,
                  color: darkModeActive ? tokens.textSecondary : "#64748B",
                }}
              >
                En fazla {section.max} kriter eklenebilir.
              </Typography>
            )}
        </Box>
      ))}
    </Box>
  );
};

export default PositionCriteria;
